import { Pulse } from 'easyship-components';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { IntlConfig, IntlProvider } from 'react-intl';

import { getLocaleFromUrl, saveLocaleToStorage } from '@/utils/locale';

import { I18nContext } from './context';
import { EasyshipLocale } from './types';
import { useGetLocalizedMessages } from './useGetLocalizedMessages';

const defaultRichTextElements: IntlConfig['defaultRichTextElements'] = {
  b: (parts) => <strong>{parts}</strong>,
  br: () => <br />,
};

interface IntlProviderProps {
  children: ReactNode;
}

const updateLocaleInUrl = (locale: EasyshipLocale) => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('locale', locale);
    window.history.replaceState({}, '', url);
  } catch {
    //
  }
};

export default function I18nProvider({ children }: IntlProviderProps) {
  const [contextLocale, setContextLocale] = useState<EasyshipLocale>(() => getLocaleFromUrl('en'));
  const { messages, locale } = useGetLocalizedMessages(contextLocale);

  const setLocale = useCallback((nextLocale: EasyshipLocale) => {
    setContextLocale(nextLocale);
    saveLocaleToStorage(nextLocale);
    updateLocaleInUrl(nextLocale);
  }, []);

  useEffect(() => {
    saveLocaleToStorage(contextLocale);
  }, [contextLocale]);

  // Don't render children until messages are loaded
  if (!messages) {
    return <Pulse />;
  }

  return (
    <I18nContext.Provider value={{ locale: contextLocale, setLocale }}>
      <IntlProvider
        locale={locale}
        messages={messages || {}}
        defaultLocale="en"
        defaultRichTextElements={defaultRichTextElements}
      >
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
}
